import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

export const IMAGEANDTEXTBLOCK_LEGACY_FIELD_ORDER = [
  "full_width",
  "image_position",
  "image_vertical_position",
  "image_size",
  "image",
  "rounded_image",
  "content"
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  imageAndTextBlockDescriptor: _generatedImageAndTextBlockDescriptor,
  imageAndTextBlockBlocDefinition,
  imageAndTextBlockDashboardLayout,
  imageAndTextBlockConfig,
  imageAndTextBlockResolveConfig,
  imageAndTextBlockProperties,
} = doodBlockConfig(
  {
    name: "Image and text",
    componentType: 'ImageAndTextBlockComponent',
    category: 'content',
    hideFromPicker: true,
    description: "Image and text.",
    keywords: [],
    sharedFields: {}
  },

  () => ({
    full_width: propertyValue({
      type: 'checkbox',
      value: false,
      options: {
        label: "pages.blocks.image-and-text.full-width.label",
      }
    }),
    image_position: propertyValue({
      type: 'select',
      value: "left",
      options: {
        label: "pages.blocks.image-and-text.image-position.label",
        multiple: false,
        choices: [
          {
            value: "left",
            label: "pages.blocks.image-and-text.image-position.options.left",
          },
          {
            value: "right",
            label: "pages.blocks.image-and-text.image-position.options.right",
          },
        ],
      }
    }),
    text_vertical_position: propertyValue({
      type: 'select',
      value: "top",
      options: {
        label: "pages.blocks.image-and-text.text-vertical-position.label",
        choices: [
          {
            value: "top",
            label: "pages.blocks.image-and-text.text-vertical-position.options.top",
          },
          {
            value: "center",
            label: "pages.blocks.image-and-text.text-vertical-position.options.center",
          },
          {
            value: "bottom",
            label: "pages.blocks.image-and-text.text-vertical-position.options.bottom",
          },
        ],
      }
    }),
    image_vertical_position: propertyValue({
      type: 'select',
      value: "top",
      options: {
        label: "pages.blocks.image-and-text.image-vertical-position.label",
        help: "pages.blocks.image-and-text.image-vertical-position.help",
        choices: [
          {
            value: "top",
            label: "pages.blocks.image-and-text.image-vertical-position.options.top",
          },
          {
            value: "center",
            label: "pages.blocks.image-and-text.image-vertical-position.options.center",
          },
          {
            value: "bottom",
            label: "pages.blocks.image-and-text.image-vertical-position.options.bottom",
          },
        ],
      }
    }),
    image_size: propertyValue({
      type: 'select',
      value: "4",
      options: {
        label: "pages.blocks.image-and-text.image-size.label",
        help: "pages.blocks.image-and-text.image-size.help",
        multiple: false,
        choices: [
          { value: "1", label: "1/12" },
          { value: "2", label: "2/12" },
          { value: "3", label: "3/12" },
          { value: "4", label: "4/12" },
          { value: "5", label: "5/12" },
          { value: "6", label: "6/12" },
          { value: "7", label: "7/12" },
          { value: "8", label: "8/12" },
          { value: "9", label: "9/12" },
          { value: "10", label: "10/12" },
          { value: "11", label: "11/12" },
        ],
      }
    }),
    image: propertyValue({
      type: 'image',
      value: '',
      options: {
        label: "pages.blocks.image-and-text.image.label",
        help: "pages.blocks.image-and-text.image.help",
      }
    }),
    rounded_image: propertyValue({
      type: 'checkbox',
      value: false,
      options: {
        label: "pages.blocks.image-and-text.rounded-image.label",
        help: "pages.blocks.image-and-text.rounded-image.help",
      }
    }),
    content: propertyValue({
      type: 'wysiwyg',
      value: '',
      options: {
        label: "pages.blocks.image-and-text.content.label",
      }
    })
  }),

  () => ({
    omitFields: ["text_vertical_position"] as const,
    tabs: [
      {
        label: "pages.blocks.image-and-text.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.image-and-text.fieldset.layout.label",
            fields: [
              "full_width",
              "image_position",
              "image_vertical_position",
            ],
          },
          {
            legend: "pages.blocks.image-and-text.fieldset.image.label",
            fields: [
              "image",
              "rounded_image",
              {
                prop: "image_size",
                visibleWhen: { rounded_image: false },
              },
            ],
          },
          {
            legend: "pages.blocks.image-and-text.fieldset.text.label",
            fields: ["content"],
          },
        ],
      },
    ],
  })
);

export function imageAndTextBlockDescriptor(): IBlockDescriptor;
export function imageAndTextBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function imageAndTextBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedImageAndTextBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(_generatedImageAndTextBlockDescriptor, imageAndTextBlockProperties, fieldOrder);
}

export {
  imageAndTextBlockBlocDefinition,
  imageAndTextBlockDashboardLayout,
  imageAndTextBlockProperties,
  imageAndTextBlockConfig,
  imageAndTextBlockResolveConfig,
};

export type ImageAndTextBlockConfig = typeof imageAndTextBlockConfig;
